import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatBRL, formatBRLouTraco, formatDateBR, formatDateTimeBR } from "../../lib/api";
import { cedentePath } from "../../lib/portal";
import { SACADO_LABEL_SUFFIX, formatCnpjPayload, maskCNPJ, maskPhoneCedente } from "../../lib/cedenteOperations";
import useCedenteNewOperationGate from "../../hooks/useCedenteNewOperationGate";
import { Plus, Search, Pencil, Trash2, TrendingUp, ArrowUpDown, History, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import StatusBadge from "../../components/StatusBadge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../components/ui/sheet";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

// Masks: `maskCNPJ` e `maskPhoneCedente` moram em `lib/cedenteOperations.js`
// (testáveis) — a página e o dialog inline do wizard gravam o MESMO cadastro,
// duas cópias divergindo produziriam formatos diferentes na mesma tabela.

export default function CedenteClientes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Gate de entrada (04(a)): o atalho "Nova operação" só existe com a feature
  // ligada E catálogo com produto habilitado.
  const { visible: newOperationVisible } = useCedenteNewOperationGate();
  const canEdit = user?.role === "cedente_admin" || user?.role === "cedente_operator";
  const isAdmin = user?.role === "cedente_admin";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("razao_social");
  const [sortDir, setSortDir] = useState("asc");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ razao_social: "", cnpj: "", contato_telefone: "", contato_email: "", status: "active" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [historyClient, setHistoryClient] = useState(null);
  const [historyOps, setHistoryOps] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/cedente/clientes");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(i => i.razao_social.toLowerCase().includes(s) || i.cnpj.includes(s));
    }
    return [...list].sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (typeof va === "string") { va = va.toLowerCase(); vb = (vb || "").toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [items, search, sortKey, sortDir]);

  const openCreate = () => {
    setEditing(null);
    setForm({ razao_social: "", cnpj: "", contato_telefone: "", contato_email: "", status: "active" });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      razao_social: c.razao_social, cnpj: c.cnpj,
      contato_telefone: c.contato_telefone || "", contato_email: c.contato_email || "", status: c.status,
    });
    setOpen(true);
  };

  const openHistory = useCallback(async (c) => {
    setHistoryClient(c);
    try {
      const { data } = await api.get(`/cedente/clientes/${c.id}/operations`);
      setHistoryOps(Array.isArray(data) ? data : []);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error("Failed to load history:", e);
      setHistoryOps([]);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/cedente/clientes/${editing.id}`, {
          razao_social: form.razao_social,
          contato_telefone: form.contato_telefone,
          contato_email: form.contato_email || null,
          status: form.status,
        });
        toast.success("Cliente atualizado");
      } else {
        await api.post("/cedente/clientes", {
          razao_social: form.razao_social,
          cnpj: formatCnpjPayload(form.cnpj),
          contato_telefone: form.contato_telefone,
          contato_email: form.contato_email || null,
          limite_credito: 0,
        });
        toast.success("Cliente criado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setSubmitting(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/cedente/clientes/${deleteTarget.id}`);
      toast.success("Cliente removido");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao remover"); }
    finally { setDeleteTarget(null); }
  };

  const SortHeader = ({ label, field, align }) => (
    <th
      className={`${align === "right" ? "text-right" : "text-left"} px-5 py-3 font-semibold cursor-pointer select-none hover:text-zinc-800 transition-colors`}
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${sortKey === field ? "text-[#0055FF]" : "text-zinc-300"}`} />
      </span>
    </th>
  );

  return (
    <div className="space-y-6" data-testid="cedente-clientes-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Cadastro</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">
            Clientes{" "}
            {/* 09.4: "bem pequeno" (literal do owner) — sufixo discreto, não um
                rename: rota, i18n e nomes de campo ficam como estão. */}
            <span className="text-[13px] font-normal text-zinc-400 align-middle">{SACADO_LABEL_SUFFIX}</span>
          </h1>
          <p className="mt-1 text-sm text-zinc-500">Seus clientes — empresas cujos recebíveis você pode antecipar.</p>
        </div>
        {canEdit && (
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white h-10 font-semibold" onClick={openCreate} data-testid="ced-new-cliente-button">
            <Plus className="h-4 w-4 mr-1.5" /> Novo Cliente
          </Button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input placeholder="Buscar por razão social ou CNPJ…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="ced-clientes-search" />
      </div>

      <div className="zemo-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/60 border-b border-[var(--zemo-border)] text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <SortHeader label="Razão Social" field="razao_social" />
                <SortHeader label="CNPJ" field="cnpj" />
                <th className="text-left px-5 py-3 font-semibold">Contato</th>
                <SortHeader label="Limite Disponível" field="limite_credito" align="right" />
                <SortHeader label="Ticket Médio" field="ticket_medio" align="right" />
                <SortHeader label="Status" field="status" />
                <SortHeader label="Cadastrado em" field="created_at" />
                <th className="text-right px-5 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--zemo-border)]">
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-zinc-500">Nenhum cliente cadastrado.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="hover:bg-zinc-50/60" data-testid={`ced-cliente-row-${c.cnpj}`}>
                  <td className="px-5 py-3.5"><div className="font-medium text-zinc-900">{c.razao_social}</div></td>
                  <td className="px-5 py-3.5 font-mono-num text-zinc-700">{c.cnpj}</td>
                  <td className="px-5 py-3.5 text-[12px] text-zinc-600">
                    <div>{c.contato_email || "—"}</div>
                    <div className="font-mono-num">{c.contato_telefone || ""}</div>
                  </td>
                  <td className="px-5 py-3.5 text-right font-mono-num font-semibold">{formatBRL(c.limite_credito)}</td>
                  <td className="px-5 py-3.5 text-right font-mono-num text-zinc-700">{formatBRL(c.ticket_medio)}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={c.status} /></td>
                  <td className="px-5 py-3.5 text-[12px] text-zinc-500">{formatDateBR(c.created_at)}</td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="text-zinc-500 hover:text-zinc-700" onClick={() => openHistory(c)} title="Histórico de recebíveis" data-testid={`ced-history-${c.cnpj}`}>
                        <History className="h-4 w-4" />
                      </Button>
                      {/* Gate de entrada (04(a)) + round 4/B2: com a feature
                          LIGADA, deep-link para o wizard (o cliente vira o
                          sacado da 1ª linha). Com ela DESLIGADA, o botão volta
                          a ser exatamente o stub que já existia — uma
                          superfície viva não pode mudar por causa de um gate
                          que está desligado. */}
                      {canEdit && (
                        newOperationVisible ? (
                          <Button size="icon" variant="ghost" className="text-[#0055FF] hover:bg-blue-50 hover:text-[#0044CC]" onClick={() => navigate(cedentePath(`/operacoes/nova?cliente_id=${c.id}`))} title="Nova operação" aria-label={`Nova operação com ${c.razao_social}`} data-testid={`ced-new-op-${c.cnpj}`}>
                            <TrendingUp className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" className="text-[#0055FF] hover:bg-blue-50 hover:text-[#0044CC]" onClick={() => toast.info(`Nova operação com ${c.razao_social} — em breve`)} title="Nova operação" data-testid={`ced-new-op-${c.cnpj}`}>
                            <TrendingUp className="h-4 w-4" />
                          </Button>
                        )
                      )}
                      {canEdit && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`ced-edit-${c.cnpj}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleteTarget(c)} data-testid={`ced-delete-${c.cnpj}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* History Sheet */}
      <Sheet open={!!historyClient} onOpenChange={(o) => !o && setHistoryClient(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="ced-history-sheet">
          <SheetHeader>
            <SheetTitle className="text-lg">Histórico de Recebíveis</SheetTitle>
          </SheetHeader>
          {historyClient && (
            <div className="mt-4 space-y-4">
              <div className="p-3 border border-[var(--zemo-border)] rounded-md bg-zinc-50/50">
                <div className="font-semibold text-zinc-900">{historyClient.razao_social}</div>
                <div className="text-[12px] text-zinc-500 font-mono-num">{historyClient.cnpj}</div>
              </div>
              {historyOps.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">Nenhum recebível operado com este cliente ainda.</p>
              ) : (
                <div className="space-y-2">
                  {historyOps.map((op) => (
                    <div key={op.id} className="p-3 border border-[var(--zemo-border)] rounded-md flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-zinc-900 font-mono-num">{op.numero}</div>
                        <div className="text-[11px] text-zinc-500">{op.product_nome} · {formatDateTimeBR(op.created_at)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold font-mono-num">{formatBRLouTraco(op.valor_liquido)}</div>
                        <StatusBadge status={op.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-zinc-400 text-center">Exibindo as operacoes mais recentes vinculadas a este cliente.</p>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ced-cliente-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle>
            <DialogDescription>{editing ? "Atualize informações do cliente." : "Cadastre um novo cliente."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Razão social</Label>
              <Input required value={form.razao_social} onChange={(e) => setForm({ ...form, razao_social: e.target.value })} data-testid="ced-cliente-form-razao" />
            </div>
            <div className="space-y-1.5">
              <Label>CNPJ</Label>
              <Input
                required disabled={!!editing}
                value={form.cnpj}
                onChange={(e) => setForm({ ...form, cnpj: maskCNPJ(e.target.value) })}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                className="font-mono-num"
                data-testid="ced-cliente-form-cnpj"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Telefone</Label>
                <Input
                  value={form.contato_telefone}
                  onChange={(e) => setForm({ ...form, contato_telefone: maskPhoneCedente(e.target.value) })}
                  placeholder="+55 11 99999-9999"
                  className="font-mono-num"
                />
              </div>
              <div className="space-y-1.5">
                <Label>E-mail</Label>
                <Input type="email" value={form.contato_email} onChange={(e) => setForm({ ...form, contato_email: e.target.value })} placeholder="contato@cliente.com.br" />
              </div>
            </div>
            {editing && (
              <div className="flex items-center justify-between rounded-md border border-[var(--zemo-border)] p-3">
                <div>
                  <div className="text-[13px] font-medium">Status ativo</div>
                  <div className="text-[11px] text-zinc-500">Apenas clientes ativos aparecem no fluxo.</div>
                </div>
                <Switch checked={form.status === "active"} onCheckedChange={(v) => setForm({ ...form, status: v ? "active" : "inactive" })} />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ced-cliente-form-submit">
                {submitting ? "Salvando…" : editing ? "Salvar" : "Criar cliente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover cliente?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação removerá <strong>{deleteTarget?.razao_social}</strong> dos seus clientes.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="ced-confirm-delete-cliente">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
